import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Activity, ArrowLeft, CheckCircle2, CloudOff, Cloud, RefreshCw, LogOut,
  Droplets, Shirt, ShowerHead, Sparkles, Utensils, Pill, Smile, Toilet, BedDouble,
  Footprints, AlertTriangle, NotebookPen, MoreHorizontal,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CareFlagsPills from "@/components/CareFlagsPills";
import PersonAvatar from "@/components/PersonAvatar";
import { asArray, ALLERGY_SEVERITY_STYLES, getAllergySeverity } from "@/lib/clientFields";

const QUEUE_KEY = "smartcare_offline_queue";
const QUEUE_MAX = 500;

const ACTIVITIES = [
  { key: "Water Refill",    Icon: Droplets,         color: "bg-sky-50 text-sky-800 border-sky-300 hover:bg-sky-100" },
  { key: "Getting Dressed", Icon: Shirt,            color: "bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100" },
  { key: "Getting Washed",  Icon: ShowerHead,       color: "bg-cyan-50 text-cyan-800 border-cyan-300 hover:bg-cyan-100" },
  { key: "Oral Care",       Icon: Sparkles,         color: "bg-teal-50 text-teal-800 border-teal-300 hover:bg-teal-100" },
  { key: "Meal",            Icon: Utensils,         color: "bg-orange-50 text-orange-800 border-orange-300 hover:bg-orange-100" },
  { key: "Medication",      Icon: Pill,             color: "bg-red-50 text-red-800 border-red-300 hover:bg-red-100" },
  { key: "Mood",            Icon: Smile,            color: "bg-purple-50 text-purple-800 border-purple-300 hover:bg-purple-100" },
  { key: "Toileting",       Icon: Toilet,           color: "bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100" },
  { key: "Sleep",           Icon: BedDouble,        color: "bg-indigo-50 text-indigo-800 border-indigo-300 hover:bg-indigo-100" },
  { key: "Activity",        Icon: Footprints,       color: "bg-pink-50 text-pink-800 border-pink-300 hover:bg-pink-100" },
  { key: "Behaviour",       Icon: AlertTriangle,    color: "bg-rose-50 text-rose-800 border-rose-300 hover:bg-rose-100" },
  { key: "Note",            Icon: NotebookPen,      color: "bg-slate-50 text-slate-800 border-slate-300 hover:bg-slate-100" },
  { key: "Other",           Icon: MoreHorizontal,   color: "bg-slate-50 text-slate-800 border-slate-300 hover:bg-slate-100" },
];

function readQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); }
  catch { return []; }
}
function writeQueue(items) { localStorage.setItem(QUEUE_KEY, JSON.stringify(items)); }

function EmarFlow({ client, onBack, onDone, careWorkerName }) {
  const [meds, setMeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMed, setSelectedMed] = useState(null);
  const [action, setAction] = useState("");
  const [reason, setReason] = useState("");
  const [signature, setSignature] = useState(careWorkerName || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/clients/${client.id}/sections/medication`)
      .then((r) => setMeds(r.data || []))
      .catch(() => setMeds([]))
      .finally(() => setLoading(false));
  }, [client.id]);

  const chooseAction = (med, act) => {
    setSelectedMed(med);
    setAction(act);
    setReason("");
  };

  const submit = async () => {
    if (!signature.trim()) {
      toast.error("Type your name to sign this eMAR entry");
      return;
    }
    if (["refused", "withheld", "prn"].includes(action) && !reason.trim()) {
      toast.error("A reason is required for this action");
      return;
    }
    setSaving(true);
    try {
      await api.post("/emar", {
        data: {
          client_id: client.id,
          medication_id: selectedMed.id,
          medication_name: selectedMed.name || selectedMed.medication_name || selectedMed.medication || "Medication",
          dose: selectedMed.dose || selectedMed.dosage || "",
          action,
          reason: reason.trim() || null,
          signature_name: signature.trim(),
          given_at: new Date().toISOString(),
        },
      });
      toast.success(`eMAR recorded: ${action}`);
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  if (selectedMed) {
    return (
      <>
        <button onClick={() => { setSelectedMed(null); setAction(""); setReason(""); }} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1" data-testid="emar-back">
          <ArrowLeft className="w-4 h-4" /> Back to medications
        </button>
        <div className="bg-white border border-slate-200 p-4 space-y-2">
          <div className="text-xs uppercase tracking-widest text-slate-500">Service user</div>
          <div className="font-medium text-slate-900">{client.full_name}</div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mt-2">Medication</div>
          <div className="font-medium text-slate-900" data-testid="emar-med-name">
            {selectedMed.name || selectedMed.medication_name || selectedMed.medication}
            {selectedMed.dose ? <span className="text-slate-500 font-normal"> · {selectedMed.dose}</span> : null}
          </div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mt-2">Action</div>
          <div className="font-medium text-slate-900 capitalize" data-testid="emar-action-label">{action}</div>
        </div>

        {["refused", "withheld", "prn"].includes(action) && (
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Reason</label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={action === "prn" ? "PRN reason (e.g. pain)" : "Reason for refusal / withhold"} className="mt-1 text-sm" data-testid="emar-reason" />
          </div>
        )}

        <div>
          <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Signature (type your name)</label>
          <input type="text" value={signature} onChange={(e) => setSignature(e.target.value)} className="w-full mt-1 h-11 px-3 border border-slate-300 bg-white text-sm" placeholder="Type your full name to sign" data-testid="emar-signature" />
        </div>

        <Button onClick={submit} disabled={saving} className="w-full h-12 bg-sky-700 hover:bg-sky-800 text-base" data-testid="emar-submit">
          <CheckCircle2 className="w-4 h-4 mr-2" />
          {saving ? "Signing…" : `Sign ${action}`}
        </Button>
      </>
    );
  }

  return (
    <>
      <button onClick={onBack} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1" data-testid="emar-back-activity">
        <ArrowLeft className="w-4 h-4" /> Choose different activity
      </button>
      <div className="bg-white border border-slate-200 p-3">
        <div className="text-xs uppercase tracking-widest text-slate-500">eMAR for</div>
        <div className="font-medium text-slate-900" data-testid="emar-client-name">{client.full_name}</div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500" data-testid="emar-loading">Loading medications…</div>
      ) : meds.length === 0 ? (
        <div className="bg-white border border-slate-200 p-6 text-center text-sm text-slate-500" data-testid="emar-empty">
          No medications on file for this service user. Add them from the desktop Service User profile.
        </div>
      ) : (
        <ul className="space-y-2" data-testid="emar-med-list">
          {meds.map((m) => {
            const label = m.name || m.medication_name || m.medication || "Medication";
            const dose = m.dose || m.dosage || "";
            return (
              <li key={m.id} className="bg-white border border-slate-200 p-3 space-y-2" data-testid={`emar-med-${m.id}`}>
                <div>
                  <div className="font-medium text-slate-900">{label}</div>
                  {dose && <div className="text-xs text-slate-500">{dose}</div>}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button size="sm" onClick={() => chooseAction(m, "administered")} data-testid={`emar-administered-${m.id}`} className="bg-emerald-700 hover:bg-emerald-800 h-10">Administered</Button>
                  <Button size="sm" variant="outline" onClick={() => chooseAction(m, "refused")} data-testid={`emar-refused-${m.id}`} className="h-10">Refused</Button>
                  <Button size="sm" variant="outline" onClick={() => chooseAction(m, "withheld")} data-testid={`emar-withheld-${m.id}`} className="h-10">Withheld</Button>
                  <Button size="sm" variant="outline" onClick={() => chooseAction(m, "prn")} data-testid={`emar-prn-${m.id}`} className="h-10">PRN</Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

export default function MobilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [safety, setSafety] = useState({});
  const [selected, setSelected] = useState(null);
  const [activity, setActivity] = useState(null);
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [pending, setPending] = useState(readQueue());
  const [online, setOnline] = useState(navigator.onLine);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const goOnline = () => setOnline(true);
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    api.get("/dashboard/client-safety").then((r) => setSafety(r.data?.clients || {})).catch(() => {});
  }, []);

  const syncQueue = useCallback(async () => {
    const q = readQueue();
    if (q.length === 0) { toast.success("Nothing to sync"); return; }
    setSyncing(true);
    try {
      const { data } = await api.post("/offline-sync", { actions: q });
      // Drop synced items
      const syncedIds = new Set((data.results || [])
        .filter((r) => r.status === "created" || r.status === "already-synced")
        .map((r) => r.client_local_id));
      const remaining = q.filter((a) => !syncedIds.has(a.client_local_id));
      writeQueue(remaining);
      setPending(remaining);
      toast.success(`Synced ${syncedIds.size} record(s)${remaining.length ? `; ${remaining.length} still pending` : ""}`);
    } catch (e) {
      toast.error("Sync failed — will retry when online");
    } finally {
      setSyncing(false);
    }
  }, []);

  useEffect(() => {
    if (online && pending.length > 0) syncQueue();
  }, [online]);

  const reset = () => { setSelected(null); setActivity(null); setTitle(""); setNotes(""); };

  const submit = async () => {
    if (!selected || !activity || !title.trim() || !notes.trim()) {
      toast.error("Fill in all fields");
      return;
    }
    setSaving(true);
    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const now = new Date();
    const isoLocal = now.toISOString().slice(0, 16);
    const action = {
      client_local_id: localId,
      kind: "daily_log",
      client_id: selected.id,
      client_generated_at: now.toISOString(),
      data: { occurred_at: isoLocal, activity, title: title.trim(), notes: notes.trim() },
    };

    // Optimistic: try to POST; on failure queue it
    try {
      if (!online) throw new Error("offline");
      await api.post("/offline-sync", { actions: [action] });
      toast.success(`${activity} logged for ${selected.full_name}`);
    } catch (e) {
      const existing = readQueue();
      if (existing.length >= QUEUE_MAX) {
        toast.error(`Offline queue is full (${QUEUE_MAX} items). Please connect and sync before logging more.`);
        setSaving(false);
        return;
      }
      const q = [...existing, action];
      writeQueue(q);
      setPending(q);
      toast.info(`Saved locally — will sync when online (${q.length} pending)`);
    }
    setSaving(false);
    reset();
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col" data-testid="mobile-view">
      {/* Header */}
      <header className="bg-slate-900 text-slate-100 px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <div className="w-8 h-8 rounded-sm bg-sky-600 flex items-center justify-center shrink-0">
          <Activity className="w-4 h-4" strokeWidth={2.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading text-sm font-semibold">Care Worker</div>
          <div className="text-[10px] text-slate-400 truncate">{user?.name || user?.email}</div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {online ? <Cloud className="w-4 h-4 text-emerald-400" /> : <CloudOff className="w-4 h-4 text-amber-400" />}
          <span className="tabular-nums">{pending.length}</span>
        </div>
        <button onClick={syncQueue} disabled={syncing} className="text-xs bg-slate-800 hover:bg-slate-700 px-2 py-1 rounded-sm" data-testid="mobile-sync">
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
        </button>
        <button onClick={() => navigate("/")} className="text-xs bg-slate-800 hover:bg-slate-700 px-2 py-1 rounded-sm" data-testid="mobile-desktop">Desktop</button>
        <button onClick={logout} className="text-xs bg-slate-800 hover:bg-slate-700 px-2 py-1 rounded-sm" data-testid="mobile-logout"><LogOut className="w-3.5 h-3.5" /></button>
      </header>

      <div className="flex-1 p-4 max-w-xl w-full mx-auto space-y-4">
        {!selected ? (
          <>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Select a service user</div>
            {clients.length === 0 ? (
              <div className="bg-white border border-slate-200 p-6 text-center text-sm text-slate-500">No service users on file.</div>
            ) : (
              <ul className="space-y-2" data-testid="mobile-clients-list">
                {clients.map((c) => (
                  <li key={c.id}>
                    <button
                      onClick={() => setSelected(c)}
                      data-testid={`mobile-client-${c.id}`}
                      className="w-full flex items-center gap-3 bg-white border border-slate-200 hover:border-sky-400 hover:bg-sky-50 p-4 text-left transition-colors"
                    >
                      <div className="w-10 h-10 shrink-0">
                        <PersonAvatar person={c} name={c.full_name} size="md" testid={`mobile-avatar-${c.id}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900">{c.full_name}</div>
                        {c.primary_needs && <div className="text-xs text-slate-500 truncate">{c.primary_needs}</div>}
                        {(safety[c.id]?.critical_allergies?.length > 0) && (
                          <div className="text-[10px] text-red-800 mt-1 inline-flex items-center gap-1" data-testid={`mobile-critical-${c.id}`}>
                            <AlertTriangle className="w-3 h-3" />
                            <span className="font-semibold uppercase tracking-widest">Severe allergy:</span>
                            {safety[c.id].critical_allergies.map((a) => a.item).join(", ")}
                          </div>
                        )}
                        {(safety[c.id]?.care_flags?.length > 0) && (
                          <div className="mt-1"><CareFlagsPills flags={safety[c.id].care_flags} size="sm" /></div>
                        )}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : !activity ? (
          <>
            <button onClick={() => setSelected(null)} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1" data-testid="mobile-back-clients">
              <ArrowLeft className="w-4 h-4" /> Choose different service user
            </button>
            <div className="bg-white border border-slate-200 p-3 flex items-center gap-3">
              <div className="w-10 h-10 shrink-0">
                <PersonAvatar person={selected} name={selected.full_name} size="md" testid="mobile-selected-avatar" />
              </div>
              <div>
                <div className="font-medium text-slate-900" data-testid="mobile-selected-client">{selected.full_name}</div>
                {asArray(selected.allergies).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {asArray(selected.allergies).map((a) => (
                      <span key={a} className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded-full border ${ALLERGY_SEVERITY_STYLES[getAllergySeverity(a)]}`}>⚠ {a}</span>
                    ))}
                  </div>
                )}
                {(safety[selected.id]?.critical_allergies?.length > 0) && (
                  <div className="mt-1 text-[11px] text-red-800 bg-red-50 border-l-2 border-red-500 px-2 py-1" data-testid="mobile-selected-critical">
                    <AlertTriangle className="w-3 h-3 inline-block mr-1" />
                    {safety[selected.id].critical_allergies.map((a, i) => (
                      <span key={i}>{i > 0 ? " · " : ""}<span className="font-semibold uppercase text-[10px] tracking-widest">{a.severity}</span> {a.item}{a.reaction ? ` — ${a.reaction}` : ""}</span>
                    ))}
                  </div>
                )}
                {(safety[selected.id]?.care_flags?.length > 0) && (
                  <div className="mt-1"><CareFlagsPills flags={safety[selected.id].care_flags} size="sm" /></div>
                )}
              </div>
            </div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">What are you logging?</div>
            <div className="grid grid-cols-2 gap-2" data-testid="mobile-activity-grid">
              {ACTIVITIES.map((a) => {
                const Icon = a.Icon;
                return (
                  <button
                    key={a.key}
                    onClick={() => { setActivity(a.key); setTitle(a.key); }}
                    data-testid={`mobile-activity-${a.key.replace(/\s+/g, "-")}`}
                    className={`p-4 border-2 text-sm font-medium flex flex-col items-center justify-center gap-2 ${a.color} transition-colors`}
                  >
                    <Icon className="w-7 h-7" strokeWidth={1.75} aria-hidden="true" />
                    <span>{a.key}</span>
                  </button>
                );
              })}
            </div>
          </>
        ) : activity === "Medication" ? (
          <EmarFlow
            client={selected}
            onBack={() => { setActivity(null); setTitle(""); setNotes(""); }}
            onDone={() => reset()}
            careWorkerName={user?.name || user?.email || ""}
          />
        ) : (
          <>
            <button onClick={() => { setActivity(null); setTitle(""); setNotes(""); }} className="text-sm text-slate-600 hover:text-slate-900 flex items-center gap-1" data-testid="mobile-back-activity">
              <ArrowLeft className="w-4 h-4" /> Choose different activity
            </button>
            <div className="bg-white border border-slate-200 p-3 space-y-1">
              <div className="text-xs uppercase tracking-widest text-slate-500">For</div>
              <div className="font-medium text-slate-900">{selected.full_name}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-2">Activity</div>
              <div className="font-medium text-slate-900">{activity}</div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Title</label>
              <input
                type="text" value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full mt-1 h-11 px-3 border border-slate-300 bg-white text-sm"
                placeholder="Short summary…"
                data-testid="mobile-title"
              />
            </div>

            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Notes</label>
              <Textarea
                rows={5} value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1 text-sm"
                placeholder="What happened, any concerns…"
                data-testid="mobile-notes"
              />
            </div>

            <Button
              onClick={submit} disabled={saving}
              className="w-full h-12 bg-sky-700 hover:bg-sky-800 text-base"
              data-testid="mobile-submit"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              {saving ? "Saving…" : `Log ${activity}`}
            </Button>

            {!online && (
              <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex items-center gap-2">
                <CloudOff className="w-4 h-4" />
                You&apos;re offline — this entry will be saved locally and synced automatically when connection returns.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
